import { BreakpointObserver } from '@angular/cdk/layout';
import { DestroyRef, Injector, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, map, startWith } from 'rxjs';

const COLLAPSED_BREAKPOINT = '(min-width: 1400px)';
const SIDEBAR_DESKTOP_BREAKPOINT = '(min-width: 1440px)';
const EXPANDED_BREAKPOINT = '(min-width: 16000px)';

export interface AuditPanelAutoOpenDeps {
  isNew: Signal<boolean>;
  canAccess: Signal<boolean>;
  isSidebarCollapsed: Signal<boolean>;
  auditPanelOpen: WritableSignal<boolean>;
  breakpointObserver: BreakpointObserver;
  destroyRef: DestroyRef;
  injector: Injector;
}

/**
 * Auto-opens the audit log panel when the viewport has enough space to render
 * the entity edit content alongside the panel.
 *
 * Uses two breakpoints depending on the main navigation sidebar state:
 * - Sidebar collapsed (or below desktop width): 1200px
 * - Sidebar expanded: 1440px (collapsed threshold + sidebar width difference)
 */
export function wireAuditPanelAutoOpen(deps: AuditPanelAutoOpenDeps): void {
  const breakpoints$ = deps.breakpointObserver.observe([COLLAPSED_BREAKPOINT, SIDEBAR_DESKTOP_BREAKPOINT, EXPANDED_BREAKPOINT]).pipe(
    map(state => ({
      atLeastCollapsed: state.breakpoints?.[COLLAPSED_BREAKPOINT] === true,
      atLeastDesktop: state.breakpoints?.[SIDEBAR_DESKTOP_BREAKPOINT] === true,
      atLeastExpanded: state.breakpoints?.[EXPANDED_BREAKPOINT] === true,
    }))
  );

  const sidebarCollapsed$ = toObservable(deps.isSidebarCollapsed, { injector: deps.injector }).pipe(startWith(deps.isSidebarCollapsed()));

  combineLatest([breakpoints$, sidebarCollapsed$])
    .pipe(takeUntilDestroyed(deps.destroyRef))
    .subscribe(([bp, sidebarCollapsed]) => {
      if (deps.isNew() || !deps.canAccess()) {
        deps.auditPanelOpen.set(false);
        return;
      }
      const sidebarExpanded = bp.atLeastDesktop && !sidebarCollapsed;
      deps.auditPanelOpen.set(sidebarExpanded ? bp.atLeastExpanded : bp.atLeastCollapsed);
    });
}
